import template from './acris-faq-list.html.twig';
import './acris-faq-list.scss';

const { Component, Mixin } = Shopware;
const { Criteria } = Shopware.Data;

Component.register('acris-faq-list', {
    template,

    inject: [
        'repositoryFactory',
        'numberRangeService',
        'filterFactory',
    ],

    mixins: [
        Mixin.getByName('listing'),
        Mixin.getByName('notification'),
        Mixin.getByName('placeholder')
    ],

    data() {
        return {
            items: null,
            isLoading: false,
            showDeleteModal: false,
            total: 0,
            page: 1,
            storeKey: 'grid.filter.acris_faq',
            activeFilterNumber: 0,
            filterCriteria: [],
            defaultFilters: [
                'active-filter',
                'internal-name-filter',
                'priority-filter',
                'saleschannel-filter',
                'rules-filter',
                'products-filter',
                'groups-filter',
            ],
        };
    },

    metaInfo() {
        return {
            title: this.$createTitle()
        };
    },

    watch: {
        faqCriteria: {
            handler() {
                this.getList();
            },
            deep: true,
        },
    },

    computed: {
        entityRepository() {
            return this.repositoryFactory.create('acris_faq');
        },

        columns() {
            return this.getColumns();
        },

        listFilterOptions() {
            return {
                'active-filter': {
                    property: 'active',
                    label: this.$tc('acris-faq.filters.activeFilter.label'),
                    placeholder: this.$tc('acris-faq.filters.activeFilter.placeholder'),
                },
                'internal-name-filter': {
                    property: 'internalName',
                    label: this.$tc('acris-faq.filters.nameFilter.label'),
                    placeholder: this.$tc('acris-faq.filters.nameFilter.placeholder'),
                },
                'priority-filter': {
                    property: 'priority',
                    label: this.$tc('acris-faq.filters.priorityFilter.label'),
                    numberType: 'int',
                    step: 1,
                    min: 0,
                    placeholder: this.$tc('acris-faq.filters.priorityFilter.placeholder'),
                },
                'saleschannel-filter': {
                    property: 'salesChannels',
                    label: this.$tc('acris-faq.filters.salesChannelFilter.label'),
                    placeholder: this.$tc('acris-faq.filters.salesChannelFilter.placeholder'),
                },
                'rules-filter': {
                    property: 'rules',
                    label: this.$tc('acris-faq.filters.ruleFilter.label'),
                    placeholder: this.$tc('acris-faq.filters.ruleFilter.placeholder'),
                },
                'products-filter': {
                    property: 'products',
                    label: this.$tc('acris-faq.filters.productFilter.label'),
                    placeholder: this.$tc('acris-faq.filters.productFilter.placeholder'),
                    criteria: this.productCriteria,
                    displayVariants: true,
                },
                'groups-filter': {
                    property: 'groups',
                    label: this.$tc('acris-faq.filters.groupFilter.label'),
                    placeholder: this.$tc('acris-faq.filters.groupFilter.placeholder'),
                },
            };
        },

        productCriteria() {
            const productCriteria = new Criteria();
            productCriteria.addAssociation('options.group');

            return productCriteria;
        },

        faqCriteria() {
            const criteria = new Criteria(this.page, this.limit);
            criteria.addAssociation('media')
                .addAssociation('groups')
                .addAssociation('salesChannels')
                .addAssociation('rules')
                .addAssociation('products');
            criteria.getAssociation('groups').addSorting(Criteria.sort('priority', 'DESC'));
            criteria.addSorting(Criteria.sort('priority', 'DESC'));
            criteria.addSorting(Criteria.sort('internalName', 'ASC'));
            criteria.setTerm(this.term);

            this.filterCriteria.forEach((filter) => {
                criteria.addFilter(filter);
            });

            return criteria;
        },

        listFilters() {
            return this.filterFactory.create('acris_faq', this.listFilterOptions);
        },
    },

    methods: {
        async getList() {
            this.isLoading = true;
            let criteria = await Shopware.Service('filterService').mergeWithStoredFilters(
                this.storeKey,
                this.faqCriteria,
            );

            this.activeFilterNumber = criteria.filters.length - 1;

            this.entityRepository.search(criteria, Shopware.Context.api).then((items) => {
                this.total = items.total;
                this.items = items;
                this.isLoading = false;

                return items;
            }).catch(() => {
                this.isLoading = false;
            });
        },

        onDelete(id) {
            this.showDeleteModal = id;
        },

        onCloseDeleteModal() {
            this.showDeleteModal = false;
        },

        getColumns() {
            return [{
                property: 'internalName',
                inlineEdit: 'string',
                label: 'acris-faq.list.columnInternalName',
                routerLink: 'acris.faq.detail',
                allowResize: true,
                primary: true
            }, {
                property: 'active',
                label: 'acris-faq.list.columnActive',
                inlineEdit: 'boolean',
                allowResize: true,
                align: 'center'
            }, {
                property: 'priority',
                inlineEdit: 'number',
                label: 'acris-faq.list.columnPriority',
                routerLink: 'acris.faq.detail',
                allowResize: true
            }, {
                property: 'name',
                inlineEdit: 'string',
                label: 'acris-faq.list.columnName',
                routerLink: 'acris.faq.detail',
                width: '250px',
                allowResize: true,
            }, {
                property: 'description',
                inlineEdit: 'string',
                label: 'acris-faq.list.columnDescription',
                routerLink: 'acris.faq.detail',
                width: '250px',
                allowResize: true
            }, {
                property: 'groups',
                dataIndex: 'groups.priority',
                inlineEdit: 'string',
                label: 'acris-faq.list.columnGroups',
                routerLink: 'acris.faq.detail',
                width: '250px',
                allowResize: true
            }];
        },

        onChangeLanguage(languageId) {
            this.getList(languageId);
        },

        getCleanText(description) {
            if (description) {
                const text = description.replace(/<\/?[^>]+(>|$)/g, '');
                return text.replace(/&(nbsp|amp|quot|lt|gt);/g, ' ');
            }
            return '';
        },

        updateCriteria(criteria) {
            this.page = 1;

            this.filterCriteria = criteria;
        },

        onDuplicate(referenceEntity) {
            const behaviour = {
                overwrites: {
                    internalName: `${referenceEntity.internalName} - ${this.$tc('global.default.copy')}`,
                    seoUrls: null,
                    createdAt: null,
                    updatedAt: null,
                },
                cloneChildren: true,
            };

            return this.entityRepository.clone(referenceEntity.id, behaviour, Shopware.Context.api)
                .then((cloneResponse) => {
                    // Fetch the cloned entity with associations
                    const criteria = new Criteria(1, 1);
                    criteria.addAssociation('groups')
                        .addAssociation('salesChannels')
                        .addAssociation('rules')
                        .addAssociation('products');

                    return this.entityRepository.get(cloneResponse.id, Shopware.Context.api, criteria)
                        .then((newEntity) => {
                            // Copy ManyToMany associations from the original entity
                            if (referenceEntity.groups && referenceEntity.groups.length > 0) {
                                newEntity.groups = referenceEntity.groups;
                            }
                            if (referenceEntity.salesChannels && referenceEntity.salesChannels.length > 0) {
                                newEntity.salesChannels = referenceEntity.salesChannels;
                            }
                            if (referenceEntity.rules && referenceEntity.rules.length > 0) {
                                newEntity.rules = referenceEntity.rules;
                            }
                            if (referenceEntity.products && referenceEntity.products.length > 0) {
                                newEntity.products = referenceEntity.products;
                            }

                            // Save the associations
                            return this.entityRepository.save(newEntity, Shopware.Context.api)
                                .then(() => {
                                    this.$router.push({ name: 'acris.faq.detail', params: { id: newEntity.id } });
                                });
                        });
                });
        },
    }
});

